import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  createEnumParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FilterListIcon from '@mui/icons-material/FilterList';
import WarningIcon from '@mui/icons-material/Warning';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvListStateMessage from '../UI/molecules/OvListStateMessage';
import OvListSortSelect from '../UI/molecules/OvListSortSelect';
import OvVideoPlacementFilterActions from '../UI/molecules/OvVideoPlacementFilterActions';
import OvHiddenUploadsNotice from '../UI/molecules/OvHiddenUploadsNotice';
import OvVideoPlacementPreview from '../UI/molecules/OvVideoPlacementPreview';
import OvVideoLibraryDropTarget from '../UI/molecules/OvVideoLibraryDropTarget';
import OvVideoLibraryDeletedNotice from '../UI/molecules/OvVideoLibraryDeletedNotice';
import OvListFilterBar from '../UI/organisms/OvListFilterBar';
import OvRecordList from '../UI/organisms/OvRecordList';
import OvRecordListPage from '../UI/organisms/OvRecordListPage';
import OvVideoListCard from '../UI/organisms/OvVideoListCard';
import OvManageVideoPlacementsDialog from '../UI/organisms/OvManageVideoPlacementsDialog';
import OvAddVideoPlacementDialog from '../UI/organisms/OvAddVideoPlacementDialog';
import OvEditVideoPlacementDialog from '../UI/molecules/OvEditVideoPlacementDialog';
import { getVideos } from '../../services/admin/admin-video.service';
import {
  NO_PLACEMENT,
  VIDEO_ORDER_BY,
  VIDEO_STATUSES,
} from '../../common/model/dto/video/video-constants';
import { AdminVideo } from '../../common/model/dto/video/admin-video';
import { PreviewAudience } from '../../common/model/type/video-placement.types';
import { VideoListFilterField } from '../../common/model/type/video-list-filter.types';
import {
  FilterModel,
  FilterOptionsState,
} from '../../common/model/ui/filter.model';
import { getListRange } from '../../common/utils/services/order-list-utils';
import { isFilterApplied } from '../../common/utils/services/list-filter-utils';
import {
  findPlacement,
  getUnknownHealthGoals,
  isSeenBy,
  toPreviewAudience,
  toPreviewParam,
} from '../../common/utils/video-placement-utils';
import {
  DEFAULT_VIDEO_ARCHIVED_FILTER,
  getVideoListFilters,
  getVideoListSortOptions,
  VIDEO_ARCHIVED_FILTERS,
} from '../../common/utils/video-list-filter-utils';
import { ADMIN_VIDEOS_KEY } from '../../common/utils/video-query-cache';
import { OrderBy } from '../../common/types';
import { VIDEO_QUERY_PARAM } from '../../common/model/ui/video-routes';
import { useVideoPermissions } from '../../hooks/use-video-permissions';
import { useVideoPlacements } from '../../hooks/use-video-placements';
import { useVideoStatusPolling } from '../../hooks/use-video-status-polling';
import { useVideoUploads } from '../../hooks/use-video-uploads';
import { useVideoStagingSession } from '../../hooks/use-video-staging-session';
import { useVideoDetailsPath } from '../../hooks/use-video-details-path';
import { useVideoListColumns } from '../../hooks/use-video-list-columns';
import Colours from '../../design-system/colours';
import Variables from '../../design-system/variables';

export const VIDEO_LIST_LIMIT = 20;

const QUERY_PARAMS = {
  status: createEnumParam([...VIDEO_STATUSES]),
  title: withDefault(StringParam, '', true),
  placement: StringParam,
  archived: withDefault(
    createEnumParam(VIDEO_ARCHIVED_FILTERS),
    DEFAULT_VIDEO_ARCHIVED_FILTER,
    true
  ),
  preview: StringParam,
  offset: withDefault(NumberParam, 0, true),
  order_by: withDefault(
    createEnumParam([...VIDEO_ORDER_BY]),
    'created_at' as const,
    true
  ),
  order_dir: withDefault(
    createEnumParam<'asc' | 'desc'>(['asc', 'desc']),
    'desc' as const,
    true
  ),
  [VIDEO_QUERY_PARAM]: StringParam,
};

const AdminVideoList: FC = () => {
  const { t } = useTranslation();
  const { canCreate, canUpdate, canDelete } = useVideoPermissions();
  const { uploads, showTray } = useVideoUploads();
  const placements = useVideoPlacements();
  const getDetailsPath = useVideoDetailsPath();
  const { openDialog } = useVideoStagingSession();
  const [isManagingPlacements, setIsManagingPlacements] = useState(false);
  const [isAddingPlacement, setIsAddingPlacement] = useState(false);
  const [orderVideo, setOrderVideo] = useState<AdminVideo | null>(null);
  const [params, setParams] = useQueryParams(QUERY_PARAMS, {
    updateType: 'replaceIn',
  });

  const isPlacementView =
    !!params.placement && params.placement !== NO_PLACEMENT;
  const audience = toPreviewAudience(params.preview);

  const query = {
    status: params.status ?? undefined,
    title: params.title || undefined,
    placement_key: params.placement ?? undefined,
    include_archived: params.archived === 'show',
    offset: params.offset,
    limit: VIDEO_LIST_LIMIT,
    order_by: isPlacementView ? ('position' as const) : params.order_by,
    order_dir: params.order_dir,
  };

  const { data, isPending, isFetching, isError, refetch } = useQuery({
    queryKey: [ADMIN_VIDEOS_KEY, query],
    queryFn: () => getVideos(query),
    placeholderData: keepPreviousData,
  });

  const videos = data?.docs ?? [];
  const hasHiddenUploads =
    !!data &&
    uploads.some(({ videoId }) => !videos.some(({ id }) => id === videoId));
  const total = data?.total ?? 0;
  useVideoStatusPolling(videos);

  const isUnseen = (video: AdminVideo) =>
    isPlacementView && !isSeenBy(video, audience);
  const onEditOrder = canUpdate ? setOrderVideo : undefined;
  const columns = useVideoListColumns({
    placements: placements.data,
    isUnseen,
    onEditOrder,
  });

  const canManagePlacements = canCreate || canUpdate || canDelete;
  const canUpload = canCreate && canUpdate;

  const getPlacementsState = (): FilterOptionsState | undefined => {
    if (placements.isPending) {
      return { status: 'pending', message: 'videos.filters.placementsLoading' };
    }

    if (placements.isError) {
      return {
        status: 'error',
        message: 'videos.filters.placementsError',
        onRetry: () => placements.refetch(),
      };
    }

    return undefined;
  };

  const filters = getVideoListFilters(
    {
      status: params.status ?? null,
      placement: params.placement ?? null,
      title: params.title,
      archived: params.archived,
    },
    placements.data,
    getPlacementsState(),
    t
  );
  const hasFilters = filters.some(isFilterApplied);

  const clearFilters = () =>
    setParams({
      status: undefined,
      title: undefined,
      placement: undefined,
      archived: undefined,
      preview: undefined,
      offset: 0,
    });

  const applyPlacement = (placement: string | undefined) =>
    setParams({
      placement,
      preview:
        placement && placement !== NO_PLACEMENT ? params.preview : undefined,
      offset: 0,
    });

  const handleFilterChange = (filter: FilterModel) => {
    const value =
      typeof filter.value === 'string' && filter.value
        ? filter.value
        : undefined;

    switch (filter.fieldName) {
      case VideoListFilterField.status:
        setParams({
          status: VIDEO_STATUSES.find((status) => status === value),
          offset: 0,
        });
        break;

      case VideoListFilterField.placement:
        applyPlacement(value);
        break;

      case VideoListFilterField.title:
        setParams({ title: value, offset: 0 });
        break;

      case VideoListFilterField.archived:
        setParams({
          archived: VIDEO_ARCHIVED_FILTERS.find(
            (archived) => archived === value
          ),
          offset: 0,
        });
        break;

      default:
        break;
    }
  };

  const setAudience = (nextAudience: PreviewAudience) =>
    setParams({ preview: toPreviewParam(nextAudience) });

  const renderFilterFooter = (filter: FilterModel, close: () => void) =>
    filter.fieldName === VideoListFilterField.placement &&
    canManagePlacements ? (
      <OvVideoPlacementFilterActions
        canAdd={canCreate}
        canManage={canManagePlacements}
        onAdd={() => {
          close();
          setIsAddingPlacement(true);
        }}
        onManage={() => {
          close();
          setIsManagingPlacements(true);
        }}
      />
    ) : null;

  const handleSort = ({ order_by, order_dir }: OrderBy) =>
    setParams({
      order_by: VIDEO_ORDER_BY.find((column) => column === order_by),
      order_dir: order_dir === 'asc' ? 'asc' : 'desc',
      offset: 0,
    });

  const openStaging = () => openDialog();

  const getResultSummary = () => {
    if (isError && !data) {
      return t('common.listResults.unavailable');
    }

    if (isPending) {
      return t('videos.list.loading');
    }

    if (!total) {
      return t('videos.list.resultNone');
    }

    return t('videos.list.resultRange', {
      ...getListRange(params.offset, VIDEO_LIST_LIMIT, total),
      total,
    });
  };

  const renderEmptyState = () => {
    if (hasFilters) {
      return (
        <OvListStateMessage
          icon={<FilterListIcon />}
          title={t(
            isPlacementView
              ? 'videos.states.noPlacementResultsTitle'
              : 'videos.states.noResultsTitle'
          )}
          description={t(
            isPlacementView
              ? 'videos.states.noPlacementResultsDescription'
              : 'videos.states.noResultsDescription'
          )}
        >
          <OvCompactButton onClick={clearFilters}>
            {t('common.actions.clearFilters')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    return (
      <OvListStateMessage
        icon={<VideoLibraryIcon />}
        title={t('videos.states.emptyTitle')}
        description={t(
          canUpload
            ? 'videos.states.emptyDescription'
            : 'videos.states.emptyReadOnlyDescription'
        )}
      >
        {canUpload && (
          <OvCompactButton icon={<CloudUploadIcon />} onClick={openStaging}>
            {t('videos.actions.upload')}
          </OvCompactButton>
        )}
      </OvListStateMessage>
    );
  };

  const renderContent = () => {
    if (isError && !data) {
      return (
        <OvListStateMessage
          isAlert
          icon={<WarningIcon />}
          title={t('videos.states.errorTitle')}
          description={t('videos.states.errorDescription')}
        >
          <OvCompactButton onClick={() => refetch()}>
            {t('common.actions.retry')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    if (isPending) {
      return null;
    }

    return (
      <>
        {isError && (
          <RefreshError role="alert">
            {t('videos.list.refreshError')}
            <OvCompactButton tone="secondary" onClick={() => refetch()}>
              {t('common.actions.retry')}
            </OvCompactButton>
          </RefreshError>
        )}
        {videos.length ? (
          <OvRecordList
            label={t('videos.list.label')}
            records={videos}
            columns={columns}
            getKey={({ id }) => id}
            getDetailsPath={({ id }) => getDetailsPath(id)}
            getRowPresentation={(video) => ({ isMuted: isUnseen(video) })}
            renderCard={(video, detailsPath = '') => (
              <OvVideoListCard
                video={video}
                detailsPath={detailsPath}
                placements={placements.data}
                upload={uploads.find(({ videoId }) => videoId === video.id)}
                isUnseen={isUnseen(video)}
                onEditOrder={onEditOrder}
              />
            )}
            orderBy={params.order_by}
            orderDir={params.order_dir}
            onSort={isPlacementView ? undefined : handleSort}
          />
        ) : (
          renderEmptyState()
        )}
      </>
    );
  };

  if (params.video) {
    return <Navigate to={getDetailsPath(params.video)} replace />;
  }

  const page = (
    <OvRecordListPage
      title={t('app.subPages.menu.videos')}
      icon={<VideoLibraryIcon />}
      headerAction={
        canUpload && (
          <OvCompactButton icon={<CloudUploadIcon />} onClick={openStaging}>
            {t('videos.actions.upload')}
          </OvCompactButton>
        )
      }
      filters={
        <OvListFilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
          renderEditorFooter={renderFilterFooter}
        >
          <OvListSortSelect
            options={getVideoListSortOptions(t)}
            orderBy={params.order_by}
            orderDir={params.order_dir}
            onChange={handleSort}
            disabledReason={
              isPlacementView ? t('videos.sort.placementReason') : undefined
            }
          />
        </OvListFilterBar>
      }
      context={
        <>
          {isPlacementView && params.placement && (
            <OvVideoPlacementPreview
              placementKey={params.placement}
              placement={findPlacement(placements.data ?? [], params.placement)}
              arePlacementsLoaded={!!placements.data}
              audience={audience}
              unknownGoals={getUnknownHealthGoals(videos, audience)}
              onAudienceChange={setAudience}
            />
          )}
          {hasHiddenUploads && (
            <OvHiddenUploadsNotice onViewUploads={showTray} />
          )}
        </>
      }
      resultSummary={getResultSummary()}
      total={total}
      limit={VIDEO_LIST_LIMIT}
      offset={params.offset}
      onPageChange={(_, page) =>
        setParams({ offset: (page - 1) * VIDEO_LIST_LIMIT })
      }
      isBusy={isFetching}
    >
      {renderContent()}
    </OvRecordListPage>
  );

  return (
    <>
      {canUpload ? (
        <OvVideoLibraryDropTarget onFiles={openDialog}>
          {page}
        </OvVideoLibraryDropTarget>
      ) : (
        page
      )}
      <OvVideoLibraryDeletedNotice />

      {canUpdate && orderVideo && (
        <OvEditVideoPlacementDialog
          video={orderVideo}
          isOpen
          isOrderFocused
          onClose={() => setOrderVideo(null)}
          onSaved={() => setOrderVideo(null)}
        />
      )}
      {canManagePlacements && (
        <OvManageVideoPlacementsDialog
          isOpen={isManagingPlacements}
          onClose={() => setIsManagingPlacements(false)}
          onShowVideos={(placement) => {
            setIsManagingPlacements(false);
            applyPlacement(placement);
          }}
        />
      )}
      {canCreate && (
        <OvAddVideoPlacementDialog
          isOpen={isAddingPlacement}
          placements={placements.data ?? []}
          onAdded={({ key }) => applyPlacement(key)}
          onClose={() => setIsAddingPlacement(false)}
        />
      )}
    </>
  );
};

export default AdminVideoList;

const RefreshError = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${Variables.spacing.SMALL};
  margin-bottom: ${Variables.spacing.SMALL};
  color: ${Colours.OV_ERROR_ACCESSIBLE};
  font-size: ${Variables.fontSizes.MEDIUM};
`;
